import React from 'react';

/* ── Charts ──────────────────────────────────────────────────────────────── */

const PMSGrowthChart = () => (
  <svg viewBox="0 0 600 350" className="rpt-chart-svg" role="img" aria-label="PMS industry growth in assets under management, 2018 to 2023">
    <text x="300" y="22" textAnchor="middle" className="chart-title">PMS Industry Growth (AUM in ₹ Lakh Crore)</text>
    {[0, 10, 20, 30].map((val) => {
      const y = 300 - (val * 270) / 35;
      return (
        <g key={val}>
          <line x1="60" y1={y} x2="570" y2={y} stroke="#e4e7ec" strokeWidth="1" />
          <text x="52" y={y + 4} textAnchor="end" className="chart-axis-label">{val}</text>
        </g>
      );
    })}
    <line x1="60" y1="30" x2="60" y2="300" stroke="#333" strokeWidth="1.5" />
    <line x1="60" y1="300" x2="570" y2="300" stroke="#333" strokeWidth="1.5" />
    <polyline points="60,238 162,223 264,207 366,161 468,107 570,53" fill="none" stroke="#2563a8" strokeWidth="2.5" />
    {[[60, 238], [162, 223], [264, 207], [366, 161], [468, 107], [570, 53]].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="5" fill="#2563a8" />
    ))}
    {['2018', '2019', '2020', '2021', '2022', '2023'].map((year, i) => (
      <text key={year} x={60 + i * 102} y="318" textAnchor="middle" className="chart-axis-label">{year}</text>
    ))}
  </svg>
);

const AvgReturnChart = () => (
  <svg viewBox="0 0 500 320" className="rpt-chart-svg" role="img" aria-label="Average return comparison between PMS and mutual funds">
    <text x="250" y="22" textAnchor="middle" className="chart-title">Average Return Comparison (%)</text>
    {[0, 2.5, 5, 7.5, 10, 12.5, 15, 17.5].map((val) => {
      const y = 270 - (val * 240) / 20;
      return (
        <g key={val}>
          <line x1="60" y1={y} x2="460" y2={y} stroke="#e4e7ec" strokeWidth="1" />
          <text x="52" y={y + 4} textAnchor="end" className="chart-axis-label">{val}</text>
        </g>
      );
    })}
    <line x1="60" y1="30" x2="60" y2="270" stroke="#333" strokeWidth="1.5" />
    <line x1="60" y1="270" x2="460" y2="270" stroke="#333" strokeWidth="1.5" />
    <rect x="110" y={270 - (18 * 240) / 20} width="100" height={(18 * 240) / 20} fill="#2b7ab5" />
    <rect x="290" y={270 - (12 * 240) / 20} width="100" height={(12 * 240) / 20} fill="#2b7ab5" />
    <text x="160" y="290" textAnchor="middle" className="chart-axis-label">PMS Returns</text>
    <text x="340" y="290" textAnchor="middle" className="chart-axis-label">Mutual Fund Returns</text>
  </svg>
);

const SentimentChart = () => (
  <svg viewBox="0 0 560 340" className="rpt-chart-svg" role="img" aria-label="Investor sentiment analysis survey results">
    <text x="280" y="22" textAnchor="middle" className="chart-title">Investor Sentiment Analysis (%)</text>
    {[0, 10, 20, 30, 40, 50, 60].map((val) => {
      const y = 280 - (val * 240) / 70;
      return (
        <g key={val}>
          <line x1="60" y1={y} x2="520" y2={y} stroke="#e4e7ec" strokeWidth="1" />
          <text x="52" y={y + 4} textAnchor="end" className="chart-axis-label">{val}</text>
        </g>
      );
    })}
    <line x1="60" y1="40" x2="60" y2="280" stroke="#333" strokeWidth="1.5" />
    <line x1="60" y1="280" x2="520" y2="280" stroke="#333" strokeWidth="1.5" />
    {[
      { label: 'Customization', val: 65, x: 80 },
      { label: 'Ownership', val: 55, x: 200 },
      { label: 'Fee Concern', val: 40, x: 320 },
      { label: 'Volatility', val: 30, x: 430 },
    ].map(({ label, val, x }) => (
      <g key={label}>
        <rect x={x} y={280 - (val * 240) / 70} width="80" height={(val * 240) / 70} fill="#2b7ab5" />
        <text x={x + 40} y={280 - (val * 240) / 70 - 8} textAnchor="middle" className="chart-value-label">{val}%</text>
        <text x={x + 40} y="298" textAnchor="middle" className="chart-axis-label">{label}</text>
      </g>
    ))}
  </svg>
);

/* ── Report ──────────────────────────────────────────────────────────────── */

const PMSReport = () => (
  <article>
    {/* Cover */}
    <header className="rpt-cover">
      <p className="rpt-cover-eyebrow">Wealth Management Research</p>
      <h1 className="rpt-cover-title">
        Portfolio Management Services (PMS)
        <br />
        In-Depth Analytical Report
      </h1>
      <hr className="rpt-cover-divider" />
      <p className="rpt-cover-sub">
        A comprehensive study on PMS, including structure, performance, risks, and investor
        considerations.
      </p>
    </header>

    {/* 1 */}
    <section className="rpt-section">
      <h2 className="rpt-section-heading">1. Introduction</h2>
      <p>
        Portfolio Management Services (PMS) are specialized investment solutions designed for
        high-net-worth individuals (HNIs). Unlike mutual funds, PMS offers direct ownership of
        securities and highly customized investment strategies.
      </p>
      <p>
        The Indian PMS industry has grown significantly due to increasing wealth, financial
        awareness, and demand for alpha generation. PMS providers are regulated by SEBI and require
        a minimum investment of ₹50 lakh.
      </p>
      <p>
        Over the last decade, there has been a <strong>clear shift from passive investing (mutual
        funds, FDs)</strong> to <strong>active and customized wealth management solutions</strong>.
      </p>
      <p>
        Rising financial literacy and access to market data have made investors more{' '}
        <strong>return-conscious and strategy-driven</strong>.
      </p>
      <blockquote className="rpt-blockquote">
        "Why do some investors consistently outperform the market while others struggle to keep up?
        The answer often lies not just in what they invest in — but how their portfolios are
        managed."
      </blockquote>
      <p>This naturally leads into PMS as the answer.</p>
    </section>

    {/* 2 */}
    <section className="rpt-section">
      <h2 className="rpt-section-heading">2. PMS Market Growth in India</h2>
      <p>
        The PMS industry in India has witnessed rapid expansion over the last decade. Increasing
        participation from HNIs and Ultra-HNIs has driven assets under management (AUM).
      </p>
      <p>Key drivers:</p>
      <ul className="rpt-list">
        <li>Rising disposable income</li>
        <li>Demand for personalized portfolios</li>
        <li>Shift from traditional investments to equity markets</li>
        <li>Professional wealth management adoption</li>
      </ul>
      <figure className="rpt-figure">
        <div className="rpt-chart-wrapper"><PMSGrowthChart /></div>
        <figcaption className="rpt-figure-caption">
          Figure 1. Growth in PMS assets under management. Figures are indicative.
        </figcaption>
      </figure>
    </section>

    {/* 3 */}
    <section className="rpt-section">
      <h2 className="rpt-section-heading">3. PMS vs Mutual Funds</h2>
      <p>PMS portfolios aim to outperform mutual funds by offering concentrated, high-conviction investments.</p>
      <div className="rpt-table-wrapper">
        <table className="rpt-table">
          <thead>
            <tr><th>Aspect</th><th>PMS</th><th>Mutual Funds</th></tr>
          </thead>
          <tbody>
            <tr><td>Approach</td><td>Concentrated, active</td><td>Diversified, passive/active</td></tr>
            <tr><td>Ownership</td><td>Direct stocks</td><td>Pooled investment</td></tr>
            <tr><td>Customization</td><td>High</td><td>Limited</td></tr>
            <tr><td>Risk</td><td>Higher</td><td>Moderate</td></tr>
          </tbody>
        </table>
      </div>
      <figure className="rpt-figure">
        <div className="rpt-chart-wrapper"><AvgReturnChart /></div>
        <figcaption className="rpt-figure-caption">
          Figure 2. Indicative average returns. Past performance does not guarantee future results.
        </figcaption>
      </figure>
    </section>

    {/* 4 */}
    <section className="rpt-section">
      <h2 className="rpt-section-heading">4. Advantages of PMS</h2>
      <p>
        Portfolio Management Services offer several distinct advantages that make them an attractive
        investment avenue for high-net-worth individuals seeking personalized and actively managed
        portfolios.
      </p>
      <p className="rpt-subheading">Core advantages</p>
      <ul className="rpt-list rpt-list--spaced">
        <li><strong>Customization:</strong> PMS provides highly tailored investment strategies based on an investor's financial goals, risk appetite, and investment horizon. Unlike mutual funds, where portfolios are standardized, PMS allows alignment with specific objectives such as wealth creation, capital preservation, or sector-specific exposure.</li>
        <li><strong>Professional expertise:</strong> Portfolios are managed by experienced fund managers and research teams who employ advanced analytical tools, market insights, and active decision-making to optimize returns.</li>
        <li><strong>Transparency:</strong> Investors have direct visibility into their portfolio holdings, transactions, and performance. Since securities are held in the investor's own demat account, there is a higher level of clarity and accountability.</li>
        <li><strong>Flexibility:</strong> PMS allows dynamic portfolio adjustments in response to market conditions, macroeconomic changes, or evolving investment strategies, enabling more agile decision-making compared to traditional investment vehicles.</li>
        <li><strong>Alpha generation potential:</strong> Through active management and high-conviction investments, PMS aims to outperform benchmark indices and generate superior risk-adjusted returns over the long term.</li>
      </ul>
    </section>

    {/* 5 */}
    <section className="rpt-section">
      <h2 className="rpt-section-heading">5. Disadvantages of PMS</h2>
      <p>
        While PMS offers significant benefits, it also comes with several limitations that investors
        must carefully evaluate:
      </p>
      <p className="rpt-subheading">Core disadvantages</p>
      <ul className="rpt-list rpt-list--spaced">
        <li><strong>High entry barrier:</strong> PMS typically requires a minimum investment of ₹50 lakh (as per regulatory norms), making it accessible only to HNIs and limiting diversification for investors with constrained capital.</li>
        <li><strong>Fee structure complexity:</strong> PMS involves multiple layers of fees, including fixed management fees, performance-based fees, brokerage charges, and custodian fees. These costs can significantly reduce net returns, especially in moderate-performing portfolios.</li>
        <li><strong>Concentration risk:</strong> PMS portfolios often hold a limited number of high-conviction stocks, which increases exposure to specific companies or sectors. Poor performance of a few holdings can disproportionately impact overall returns.</li>
        <li><strong>Manager dependency:</strong> Portfolio performance is heavily dependent on the expertise, decision-making ability, and consistency of the fund manager. A change in management or strategy can lead to unpredictable outcomes.</li>
        <li><strong>Liquidity constraints:</strong> Unlike mutual funds, PMS investments may not offer immediate liquidity. Exit timelines can vary depending on the underlying assets and market conditions.</li>
      </ul>
    </section>

    {/* 6 */}
    <section className="rpt-section">
      <h2 className="rpt-section-heading">6. Risk Analysis</h2>
      <p>
        PMS involves multiple layers of risk, given its active management style and relatively
        concentrated portfolio structure:
      </p>
      <p className="rpt-subheading">Core risks</p>
      <ul className="rpt-list rpt-list--spaced">
        <li><strong>Market risk:</strong> Exposure to equity market volatility can significantly impact portfolio value, especially during macroeconomic downturns or market corrections.</li>
        <li><strong>Liquidity risk:</strong> Difficulty in exiting positions, particularly in mid-cap or small-cap stocks, may lead to delays or unfavorable exit prices.</li>
        <li><strong>Strategy risk:</strong> Poor execution or incorrect investment decisions by the fund manager can lead to underperformance.</li>
        <li><strong>Behavioral risk:</strong> Investor reactions such as panic selling during downturns or unrealistic expectations during bull markets can negatively impact returns.</li>
      </ul>
      <p className="rpt-subheading">Risk mitigation strategies</p>
      <p>To manage these risks effectively, investors should:</p>
      <ul className="rpt-list rpt-list--spaced">
        <li>Ensure <strong>adequate diversification</strong> within and across asset classes</li>
        <li>Adopt a <strong>long-term investment horizon (5+ years)</strong></li>
        <li>Evaluate <strong>risk-adjusted performance metrics</strong>, not just returns</li>
        <li>Select PMS providers with <strong>consistent track records and transparent processes</strong></li>
        <li>Align investments with <strong>personal risk tolerance and financial goals</strong></li>
      </ul>
    </section>

    {/* 7 */}
    <section className="rpt-section">
      <h2 className="rpt-section-heading">7. Investor Survey Insights</h2>
      <p>Survey-based observations:</p>
      <ul className="rpt-list">
        <li>65% prefer PMS for customization</li>
        <li>55% value ownership transparency</li>
        <li>40% worry about fees</li>
        <li>30% are concerned about volatility</li>
      </ul>
      <p>This reflects a trade-off between personalization and cost sensitivity.</p>
      <figure className="rpt-figure">
        <div className="rpt-chart-wrapper"><SentimentChart /></div>
        <figcaption className="rpt-figure-caption">Figure 3. Investor sentiment towards PMS.</figcaption>
      </figure>
    </section>

    {/* 8 */}
    <section className="rpt-section">
      <h2 className="rpt-section-heading">8. Key Considerations Before Investing</h2>
      <ul className="rpt-list">
        <li>Evaluate past performance across market cycles</li>
        <li>Understand fee structure clearly</li>
        <li>Check portfolio concentration</li>
        <li>Verify regulatory compliance (SEBI)</li>
        <li>Align with financial goals and risk tolerance</li>
        <li>Review reporting transparency</li>
      </ul>
    </section>

    {/* 9 */}
    <section className="rpt-section">
      <h2 className="rpt-section-heading">9. Case Insight</h2>
      <p>
        Example: a PMS focused on mid-cap equities delivered 22% CAGR over 5 years but experienced
        sharp drawdowns during market corrections.
      </p>
      <p>This highlights the importance of long-term commitment and risk appetite alignment.</p>
    </section>

    {/* 10 */}
    <section className="rpt-section">
      <h2 className="rpt-section-heading">
        10. Critical Considerations Before Investing in PMS (Common Mistakes &amp; Red Flags)
      </h2>
      <p>
        Investing in PMS requires careful evaluation beyond just returns. Many investors —
        especially first-time HNIs — make avoidable mistakes that can significantly impact long-term
        outcomes. This section outlines key factors to evaluate and highlights common pitfalls.
      </p>

      <h3 className="rpt-subsection-heading">10.1 Over-reliance on past returns</h3>
      <p>Many investors select a PMS purely based on historical performance. High past returns may be driven by short-term sectoral rallies or concentrated bets. Investors should evaluate performance across multiple market cycles, focus on consistency, and analyze drawdowns and recovery periods.</p>

      <h3 className="rpt-subsection-heading">10.2 Ignoring risk metrics</h3>
      <p>Focusing only on returns without understanding risk can lead to capital erosion during corrections. Investors should evaluate risk-adjusted returns such as Sharpe ratio, Sortino ratio, and maximum drawdown before investing.</p>

      <h3 className="rpt-subsection-heading">10.3 Lack of understanding of fee structure</h3>
      <p>PMS fee structures include management fees, performance fees, brokerage, and exit loads. Ignoring how fees compound over time significantly reduces long-term returns. Investors should compare post-fee return scenarios and understand the high watermark principle.</p>

      <h3 className="rpt-subsection-heading">10.4 Misalignment with risk appetite</h3>
      <p>Choosing aggressive strategies such as small-cap or thematic portfolios without proper risk tolerance often leads to panic selling. Investors should clearly define their investment horizon (preferably 5+ years) and select strategies aligned with their risk profile.</p>

      <h3 className="rpt-subsection-heading">10.5 Blind trust in fund manager reputation</h3>
      <p>Reputation alone should not drive investment decisions. Investors should evaluate the fund manager's investment philosophy, portfolio discipline, and transparency in communication.</p>

      <h3 className="rpt-subsection-heading">10.6 Portfolio concentration risk</h3>
      <p>PMS portfolios usually hold 10–25 stocks. While this increases return potential, it also increases downside risk. Investors should review top holdings concentration and sector allocation before investing.</p>

      <h3 className="rpt-subsection-heading">10.7 Lack of liquidity awareness</h3>
      <p>Unlike mutual funds, PMS portfolios may involve less liquid stocks. Investors should understand exit terms, lock-in conditions, and liquidity of underlying assets.</p>

      <h3 className="rpt-subsection-heading">10.8 Inadequate due diligence</h3>
      <p>Relying only on distributors or relationship managers may result in biased recommendations. Investors should conduct independent research and read the detailed disclosure document (DD).</p>

      <h3 className="rpt-subsection-heading">10.9 Unrealistic return expectations</h3>
      <p>Expecting consistent 20–25% annual returns is unrealistic. PMS returns are market-linked and cyclical. A realistic long-term expectation for equity PMS is 12–18%.</p>

      <h3 className="rpt-subsection-heading">10.10 Behavioral mistakes</h3>
      <p>Emotional decisions such as investing at market peaks, exiting during downturns, or frequently switching PMS strategies can reduce long-term wealth creation. Discipline and patience are essential.</p>

      <h3 className="rpt-subsection-heading">10.11 Analytical summary</h3>
      <div className="rpt-table-wrapper">
        <table className="rpt-table rpt-table--summary">
          <thead>
            <tr><th>Common mistake</th><th>Associated risk</th><th>Recommended action</th></tr>
          </thead>
          <tbody>
            <tr><td>Chasing past returns</td><td>Entering at market peak</td><td>Evaluate multi-cycle performance</td></tr>
            <tr><td>Ignoring risk metrics</td><td>High volatility &amp; drawdowns</td><td>Check Sharpe ratio &amp; max drawdown</td></tr>
            <tr><td>Not understanding fees</td><td>Reduced long-term returns</td><td>Analyze post-fee returns</td></tr>
            <tr><td>Wrong risk profile</td><td>Panic selling</td><td>Align with investment horizon</td></tr>
            <tr><td>Blind trust in reputation</td><td>Manager underperformance risk</td><td>Evaluate process, not brand</td></tr>
            <tr><td>High concentration</td><td>Large capital loss in few stocks</td><td>Review top holdings allocation</td></tr>
            <tr><td>Liquidity ignorance</td><td>Difficulty exiting investments</td><td>Check lock-in &amp; liquidity</td></tr>
            <tr><td>No due diligence</td><td>Wrong product selection</td><td>Read disclosure documents</td></tr>
            <tr><td>Unrealistic expectations</td><td>Premature exit</td><td>Focus on long-term returns</td></tr>
            <tr><td>Behavioral mistakes</td><td>Loss of compounding benefits</td><td>Maintain discipline</td></tr>
          </tbody>
        </table>
      </div>

      <div className="rpt-callout">
        <h3 className="rpt-callout-title">Key takeaway</h3>
        <p>
          Successful PMS investing is not just about selecting the "best-performing" portfolio — it
          is about aligning the product with your financial goals, understanding risks, and
          maintaining long-term discipline. Most investor losses arise not from poor PMS strategies,
          but from <strong>poor decision-making and lack of awareness</strong>.
        </p>
      </div>
    </section>

    <footer className="rpt-disclaimer">
      <p>
        This report is for general information and educational purposes only. It is not investment,
        financial, legal or tax advice, and it is not a recommendation to buy or sell any security.
        Markets involve risk, including loss of principal. Please consult a SEBI-registered
        investment adviser before making any investment decision.
      </p>
    </footer>
  </article>
);

export default PMSReport;
